import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { map, retry } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LocalService } from './local.service';
import { SessionService } from './session.service';
import { GPT_FEATURE } from '../utilities/featureFlags';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  user: any;
  agentData: any;
  superAdminCompany: string;
  availabilityStatus: string;


  URL = `${environment.baseServerURL}/auth`;
  private currentUserSubject: BehaviorSubject<any>;
  private featureFlagsRequests = new Map<number, Promise<string[]>>();
  private tokenExp = 0;

  constructor(
    private sessionService: SessionService,
    private http: HttpClient,
    private router: Router,
    private localService: LocalService,
  ) {
    this.currentUserSubject = new BehaviorSubject<any>(this.localService.getJsonValue('userData'));
    this.retrieveUser();
  }

  public get currentUserValue(): any {
    return this.currentUserSubject.value;
  }

  login(email: string, password: string): Observable<any> {
    return this.http.post(`${this.URL}/login`, {
      username: email,
      password
    }, {
      withCredentials: true
    }).pipe(map(user => {
      this.tokenExp = 0;
      this.currentUserSubject.next(user);
      return user;
    }));
  }

  validateSession(): Observable<any> {
    return this.http.post(`${this.URL}/check`, {}, {
      withCredentials: true,
    }).pipe(map(validate => {
      return validate;
    }));
  }

  logout() {
    if (this.user.role.id === 5) {
      this.sessionService.setOffline.emit(this.user);
    } else {
      setTimeout(() => {
        this.http.post(`${this.URL}/logout`, {}, {
          withCredentials: true
        }).subscribe(
          (_) => {
            this.localService.removeJsonValue('featureFlags');
            this.localService.removeJsonValue('userData');
            this.localService.removeJsonValue('messages');
            this.localService.removeJsonValue('sessionId');
            this.localService.removeJsonValue('superAdminCompany');
            sessionStorage.clear();
            this.user = {};
            this.router.navigate(['/login']);
            window.location.reload();
          },
          (error) => {
            console.error(error);
          });
      }, 100);
    }
  }

  sendRecoverEmail(emailAddress: string): Observable<any> {
    return this.http.post(`${this.URL}/recoveryPassword`, {
      email: emailAddress
    });
  }

  checkRecoveryToken(token: string): Observable<any> {
    return this.http.post(`${this.URL}/checkToken`, {
      token
    });
  }

  resetPassword(email: string, password: string, token: string): Observable<any> {
    return this.http.post(`${this.URL}/resetPassword`, {
      email,
      password,
      token
    });
  }

  changeCompany(company) {
    this.sessionService.changeCompany.emit(company.id);
    this.user.company = company;
    this.user.bot = company.bots[0];
    sessionStorage.clear();
    this.persistUser(this.user);
  }

  persistUser(userData) {
    this.user = userData;
    this.localService.setJsonValue('userData', userData);
    if (this.user.role.name === 'superadmin') {
      this.superAdminCompany = this.user.company;
    }
    this.localService.setJsonValue('superAdminCompany', this.superAdminCompany);
  }

  updateToken(token: string, exp: number): boolean {
    const current = this.currentUserSubject.value;
    if (!token || !current || !current.token || current.token === token) {
      return false;
    }
    if (exp * 1000 <= Date.now()) {
      return false;
    }
    // A header replayed from an HTTP cache carries an exp older than the token already
    // in use; compare against the current token's own exp, not just the in-memory
    // tokenExp, since that gets reset to 0 on login/reload.
    const currentTokenExp = this.decodeJwtExp(current.token);
    if (currentTokenExp !== null) {
      if (exp <= currentTokenExp) {
        return false;
      }
    } else if (this.tokenExp && exp <= this.tokenExp) {
      return false;
    }
    this.tokenExp = exp;
    const updated = { ...current, token };
    this.currentUserSubject.next(updated);
    if (this.user && typeof this.user === 'object') {
      this.user.token = token;
    }
    const stored = this.localService.getJsonValue('userData');
    if (stored && typeof stored === 'object') {
      this.localService.setJsonValue('userData', { ...stored, token });
    }
    return true;
  }

  private decodeJwtExp(token: string): number | null {
    try {
      const payloadSegment = token.split('.')[1];
      if (!payloadSegment) {
        return null;
      }
      const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));
      return typeof payload.exp === 'number' ? payload.exp : null;
    } catch {
      return null;
    }
  }

  retrieveUser() {
    let storedData = this.localService.getJsonValue('userData');
    storedData = storedData != null ? storedData : '{}';
    this.user = storedData;
  }

  getUser() {
    return this.localService.getJsonValue('userData');
  }

  restoreSuperAdminCompany() {
    this.user.company = this.superAdminCompany;
    this.localService.setJsonValue('userData', this.user);
  }

  getFeatureFlagsByCompany(): Observable<any> {
    const URL = `${environment.baseServerURL}/security/featureFlags/${this.user.company.id}`;
    return this.http.get(URL, {
      withCredentials: true
    });
  }

  async getFeatureFlags(): Promise<string[]> {
    const companyId = Number(this.user && this.user.company && this.user.company.id);
    const pendingRequest = this.featureFlagsRequests.get(companyId);
    if (pendingRequest) {
      return pendingRequest;
    }

    const request = this.loadFeatureFlags(companyId);
    this.featureFlagsRequests.set(companyId, request);

    try {
      return await request;
    } finally {
      this.featureFlagsRequests.delete(companyId);
    }
  }

  private async loadFeatureFlags(companyId: number): Promise<string[]> {
    const cachedFeatureFlags = this.getCachedFeatureFlags(companyId);

    try {
      const featureFlags = await this.getFeatureFlagsByCompany()
        .pipe(retry(2))
        .toPromise();
      const normalizedFeatureFlags = this.ensureConfiguredTechnologyFlags(
        Array.isArray(featureFlags) ? featureFlags : []
      );
      this.cacheFeatureFlags(companyId, normalizedFeatureFlags);
      return normalizedFeatureFlags;
    } catch (error) {
      console.error('Error fetching feature flags:', error);
      return this.ensureConfiguredTechnologyFlags(cachedFeatureFlags);
    }
  }

  private getCachedFeatureFlags(companyId: number): string[] {
    const featureFlagsByCompany = this.localService.getJsonValue('featureFlags');
    if (!featureFlagsByCompany || typeof featureFlagsByCompany !== 'object') {
      return [];
    }

    const featureFlags = featureFlagsByCompany[companyId];
    return Array.isArray(featureFlags) ? featureFlags : [];
  }

  private cacheFeatureFlags(companyId: number, featureFlags: string[]): void {
    const storedFeatureFlags = this.localService.getJsonValue('featureFlags');
    const featureFlagsByCompany = storedFeatureFlags && typeof storedFeatureFlags === 'object'
      ? storedFeatureFlags
      : {};
    featureFlagsByCompany[companyId] = featureFlags;
    this.localService.setJsonValue('featureFlags', featureFlagsByCompany);
  }

  private ensureConfiguredTechnologyFlags(featureFlags: string[]): string[] {
    const technologyType = Number(this.user && this.user.bot && this.user.bot.technologyType);
    const botUsesGpt = technologyType === 2 || technologyType === 3 || technologyType === 4 || technologyType === 5;
    if (!botUsesGpt || featureFlags.includes(GPT_FEATURE)) {
      return featureFlags;
    }

    return featureFlags.concat(GPT_FEATURE);
  }
}
